import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { IAsset } from '../../../types/model';
import { useMarketsContext } from '../MarketContext';

import { getURI } from '../../Widget/TechnicalIndicator/helpers';
import TimeFrameSelector from '../../Widget/TechnicalIndicator/TimeFrameSelector';

import { Debug } from '../../Debug/Debug';
import { useThemeContext } from '../../../context/Theme';
import { toLower } from 'lodash';
import { TUIMode } from '../../../types/model';
import { Asset } from '../../Asset/Asset';

const TIMEFRAMES = ['1D', '1W', '1M'];

const TRADING_VIEW_DARK_COLOR = '#1E222D';

export const MarketTechnicalIndicator = ({
  asset,
  height = 1,
}: {
  asset?: Pick<IAsset, 'id' | 'exchange' | 'logo' | 'symbol' | 'name' | 'type'>;
  height?: number;
}) => {
  const params = useParams();
  const { assetsList } = useMarketsContext();
  const { theme } = useThemeContext();

  const { id, logo, name, symbol, type, exchange } = asset ??
    assetsList.find(({ id: assetId }) => assetId === params?.id) ?? {
      id: '',
      exchange: '',
      logo: '',
      symbol: '',
      name: '',
      type: 'CRYPTO',
    };

  const [selectedTimeFrame, setSelectedTimeFrame] = useState(TIMEFRAMES[0]);

  const exchangeSymbol =
    type === 'FOREX'
      ? `FX_IDC:${symbol}USD`
      : type === 'CRYPTO'
      ? `${symbol}USD`
      : `${exchange}:${symbol}`;

  const uri = getURI({
    interval: selectedTimeFrame,
    exchangeSymbol,
    theme: toLower(theme) as Lowercase<TUIMode>,
  });

  return (
    <div className={'flex'}>
      <div className={`flex w-full flex-col h-120 relative overflow-hidden`}>
        <div className={'user-select-none overflow-hidden pointer-events-none'}>
          <iframe
            className="w-full h-120"
            title={`iframe-${selectedTimeFrame}-${exchangeSymbol}`}
            src={uri}
            loading={'lazy'}
          />
        </div>
        <div
          className={`
            absolute
            h-9
            bg-white
            bottom-0
            left-0
            right-0
            ${
              theme !== 'DARK' &&
              `border-b
              border-l
              border-r
            border-grey-10`
            }
          `}
        ></div>
        <div
          className={`
            absolute
            h-28
            bg-white
            top-0
            left-0
            right-0
            ${
              theme !== 'DARK' &&
              `border-l
            border-t
            border-grey-10`
            }
          `}
        >
          <div
            className={`flex items-center pl-5 gap-3 h-20`}
            style={{
              background:
                theme === 'DARK' ? TRADING_VIEW_DARK_COLOR : 'transparent',
            }}
          >
            <Asset
              id={id}
              logo={logo}
              name={name}
              symbol={symbol}
              type={type || ''}
              exchange={exchange}
              size={'large'}
            />

            <div className="ml-auto">
              <TimeFrameSelector
                timeFrames={TIMEFRAMES}
                selected={selectedTimeFrame}
                onChange={setSelectedTimeFrame}
              />
            </div>
          </div>

          <a
            href="https://www.tradingview.com"
            target="_blank"
            rel="noopener noreferrer"
            data-target-type="copyright"
            className={'h-8 flex items-center pl-4 justify-between'}
          >
            <span className={'text-xs'}>
              <span className={'text-blue'}>
                {symbol.toUpperCase()} stock analysis
              </span>{' '}
              <span className={'text-grey-30'}>by TradingView</span>
            </span>
            <span
              className={'flex items-center justify-end'}
              style={{
                border: '1px solid #eee',
                borderBottomLeftRadius: '14px',
                borderTopLeftRadius: '14px',
                borderTop: '1px solid #eee',
                padding: '4px 0 4px 6px',
                borderBottom: '1px solid #eee',
                borderLeft: '1px solid #eee',
                boxShadow: '1px 1px 3px #eee',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 16"
                width="32"
                height="16"
                className={'h-3 mr-1'}
              >
                <path d="M12 0H0v6h6v10h6V0zm14 16h-8l6-16h8l-6 16zM17 6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 65 14"
                width="65"
                height="14"
                className={'mr-2'}
              >
                <path d="M24.63 4.5a.83.83 0 0 1-.57-.2.72.72 0 0 1-.24-.55c0-.2.08-.38.24-.52a.81.81 0 0 1 .57-.23c.23 0 .42.08.58.23a.73.73 0 0 1 0 1.07.8.8 0 0 1-.58.2zM24.03 5.63h1.3v5.12h-1.3V5.63zM18.07 10.55c.33.14.68.2 1.06.2.4 0 .75-.07 1.05-.23l.18-.09c.23-.11.4-.2.53-.36v.68h1.14V3.3H20.9V6.3c-.16-.2-.39-.48-.7-.64-.31-.16-.67-.25-1.06-.25a2.62 2.62 0 0 0-2.46 1.63c-.13.33-.2.67-.2 1.04a2.7 2.7 0 0 0 .76 1.89c.24.24.52.43.84.57zM20 9.61c-.2.08-.42.12-.66.12a1.8 1.8 0 0 1-.68-.12c-.2-.09-.37-.2-.51-.35a1.7 1.7 0 0 1-.33-1.82 1.54 1.54 0 0 1 .85-.86c.2-.09.42-.13.67-.13.24 0 .46.04.66.13.2.08.38.2.52.35.14.15.26.32.34.51.08.2.12.42.12.65a1.7 1.7 0 0 1-.46 1.17 1.6 1.6 0 0 1-.52.35zM0 4.45h2.27v6.3h1.29v-6.3H6V3.3H0v1.15zM7.2 5.63L6 5.65v5.1h1.2v-2.5c0-.25.04-.48.11-.68a1.32 1.32 0 0 1 .7-.79c.17-.06.34-.1.52-.1.2 0 .27.02.45.08l.03.01V5.62a1.5 1.5 0 0 0-.81.03 1.56 1.56 0 0 0-.77.5c-.1.11-.17.24-.23.39v-.91zM12.25 10.76c-.37 0-.72-.06-1.05-.2a2.7 2.7 0 0 1-1.4-1.43 2.7 2.7 0 0 1-.2-1.03c0-.37.06-.72.2-1.04a2.75 2.75 0 0 1 1.4-1.41c.33-.15.68-.22 1.05-.22.4 0 .75.08 1.06.25.32.15.55.43.7.63v-.68h1.22v5.13h-1.21v-.7c-.13.18-.32.27-.56.39l-.15.08c-.3.16-.66.23-1.06.23zm.22-1.02a1.6 1.6 0 0 0 1.64-1.64c0-.24-.04-.45-.12-.65a1.5 1.5 0 0 0-.34-.51 1.4 1.4 0 0 0-.52-.35 1.6 1.6 0 0 0-.66-.13 1.68 1.68 0 0 0-1.19.48 1.7 1.7 0 0 0-.45 1.16 1.7 1.7 0 0 0 .45 1.17c.15.14.32.26.52.35.2.08.43.12.67.12zM28.25 5.63h-1.21v5.12h1.2l.01-2.52c0-.55.14-.97.4-1.25.28-.29.62-.43 1.02-.43.35 0 .63.11.84.35.2.22.3.54.3.95v2.9h1.22V7.67c0-.33-.05-.63-.16-.89a1.8 1.8 0 0 0-1.09-1.1 2.3 2.3 0 0 0-.86-.16c-.36 0-.68.07-.98.2-.29.14-.52.34-.69.6v-.7zM35.5 13c-.55 0-1.05-.09-1.5-.26-.45-.17-.81-.4-1.09-.68l.74-.81a2.51 2.51 0 0 0 1.83.74c.23 0 .44-.03.64-.1s.38-.17.53-.3c.15-.14.27-.31.36-.5.09-.2.13-.44.13-.7v-.47a2 2 0 0 1-.72.58 2.78 2.78 0 0 1-2.08.03c-.32-.14-.6-.32-.84-.56a2.79 2.79 0 0 1-.56-.83 2.67 2.67 0 0 1 .56-2.86 2.66 2.66 0 0 1 2.92-.53c.31.15.55.34.72.56v-.68h1.22v4.78c0 .44-.08.83-.22 1.15-.15.33-.35.6-.61.81-.25.22-.55.37-.9.48-.36.1-.73.15-1.14.15zm.1-3.31a1.71 1.71 0 0 0 1.17-.44c.15-.14.26-.3.34-.5a1.55 1.55 0 0 0 0-1.26 1.4 1.4 0 0 0-.34-.5 1.57 1.57 0 0 0-.52-.32c-.2-.08-.41-.12-.65-.12-.23 0-.45.04-.65.12a1.52 1.52 0 0 0-1 1.45c.01.22.05.44.13.63a1.55 1.55 0 0 0 .87.82c.2.08.42.12.65.12zM40.04 3.3h-1.4l3.28 7.46h1.14l3.26-7.46H44.9l-2.42 5.62-2.44-5.62zM47.81 4.5a.83.83 0 0 1-.57-.2.72.72 0 0 1-.24-.55c0-.2.08-.38.24-.52A.81.81 0 0 1 47.8 3c.23 0 .42.08.57.23.16.14.23.31.23.52 0 .21-.07.4-.23.54a.8.8 0 0 1-.57.22zM47.07 5.63h1.35v5.12h-1.35V5.63zM51.88 10.57c.35.13.74.2 1.15.2.58 0 1.08-.11 1.5-.32.43-.21.77-.5 1.02-.87l-.93-.62c-.15.23-.35.41-.6.56-.26.15-.59.22-.97.22-.23 0-.45-.03-.65-.1-.2-.06-.38-.16-.53-.28a1.57 1.57 0 0 1-.37-.44 1.46 1.46 0 0 1-.15-.58h4.33l.01-.2v-.3c0-.4-.07-.91-.21-1.22a2.4 2.4 0 0 0-1.42-1.3c-.32-.1-.67-.16-1.03-.16-.41 0-.8.07-1.15.2a2.68 2.68 0 0 0-1.5 1.42 3.57 3.57 0 0 0-.01 2.4c.15.32.34.6.6.85.25.23.56.41.9.54zm2.06-4.16c.28.2.46.47.53.84H51.4c.05-.19.12-.35.22-.5a1.46 1.46 0 0 1 .84-.57c.18-.04.35-.07.53-.07.35 0 .67.1.95.3zM59.6 10.76l1.2-3.4 1.2 3.4h1.15L65 5.49h-1.29L62.5 9.12 61.25 5.5h-.92l-1.27 3.63-1.19-3.63H56.6l1.85 5.27h1.14z"></path>
              </svg>
            </span>
          </a>
        </div>
        <Debug data={{ exchangeSymbol, uri }} />
      </div>
    </div>
  );
};
